import { useState, useCallback } from "react";
import * as tus from "tus-js-client";
import { apiRequest } from "@/lib/queryClient";

const MAX_RETRIES = 2;
// Transfer-stall timer: reset on every progress event. Catches a dead connection
// regardless of file size, since a healthy transfer keeps producing progress events.
const TRANSFER_STALL_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes with no progress
// Response-wait timer: starts once all bytes are sent and we're just waiting on
// Cloudflare's HTTP response (this is the "stuck on Processing video..." window).
const RESPONSE_WAIT_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
// Native xhr.timeout backstop, in case lengthComputable is ever false and the
// custom timers above never engage.
const ABSOLUTE_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes
const ACCEPTED_TYPES = ["video/", "audio/"];
const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024 * 1024; // 4 GB

interface UploadResponse {
  cloudflareStreamId: string;
  metadata: {
    name: string;
    size: number;
    contentType: string;
  };
}

interface UseUploadOptions {
  onSuccess?: (response: UploadResponse) => void;
  onError?: (error: Error) => void;
}

function validateFile(file: File): string | null {
  if (!ACCEPTED_TYPES.some((t) => file.type.startsWith(t))) {
    return `Unsupported file type "${file.type || "unknown"}". Please upload a video file.`;
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `File is too large (${(file.size / (1024 * 1024 * 1024)).toFixed(1)} GB). Maximum size is 4 GB.`;
  }
  return null;
}

function xhrUpload(
  uploadURL: string,
  file: File,
  onProgress: (pct: number) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", uploadURL, true);
    xhr.timeout = ABSOLUTE_TIMEOUT_MS;

    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    let responseTimer: ReturnType<typeof setTimeout> | undefined;
    // Distinguishes our own timeout-triggered aborts from a genuine user cancel,
    // since both go through xhr.onabort.
    let timeoutKind: "stall" | "response" | null = null;

    const clearTimers = () => {
      clearTimeout(stallTimer);
      clearTimeout(responseTimer);
    };

    const armStallTimer = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        timeoutKind = "stall";
        xhr.abort();
      }, TRANSFER_STALL_TIMEOUT_MS);
    };

    armStallTimer();

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress(Math.round((event.loaded / event.total) * 100));
      if (event.loaded >= event.total) {
        clearTimeout(stallTimer);
        if (!responseTimer) {
          responseTimer = setTimeout(() => {
            timeoutKind = "response";
            xhr.abort();
          }, RESPONSE_WAIT_TIMEOUT_MS);
        }
      } else {
        armStallTimer();
      }
    };

    xhr.onload = () => {
      clearTimers();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed (HTTP ${xhr.status}). Please try again.`));
      }
    };

    xhr.onerror = () => {
      clearTimers();
      reject(new Error("Network error during upload. Check your connection and try again."));
    };

    xhr.onabort = () => {
      clearTimers();
      // Both messages below intentionally contain "timed out" — the retry-exclusion
      // check in uploadFile() keys off that substring to avoid blindly re-uploading
      // the whole file after a timeout. Keep the two in sync if wording changes.
      if (timeoutKind === "stall") {
        reject(new Error("Upload stalled and timed out: no data was sent for several minutes. Check your connection and try again."));
      } else if (timeoutKind === "response") {
        reject(new Error("Upload timed out waiting for the server to finish processing your file. This can happen with very large files — please try again."));
      } else {
        reject(new Error("Upload was cancelled."));
      }
    };

    xhr.ontimeout = () => {
      clearTimers();
      reject(new Error("Upload timed out waiting for the server to finish processing your file. This can happen with very large files — please try again."));
    };

    if (signal) {
      signal.addEventListener("abort", () => xhr.abort());
    }

    xhr.send(formData);
  });
}

// Used for files above the server's TUS threshold (see TUS_THRESHOLD_BYTES in
// server/routes.ts) — Cloudflare rejects plain POST uploads over 200MB. The
// server has already provisioned the one-time TUS endpoint; we resume/PATCH
// against it directly rather than creating a new upload.
function tusUpload(
  tusEndpoint: string,
  file: File,
  onProgress: (pct: number) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      uploadUrl: tusEndpoint,
      chunkSize: 50 * 1024 * 1024, // 50MB, Cloudflare's recommended value
      retryDelays: [0, 3000, 5000, 10000, 20000],
      onProgress: (bytesUploaded, bytesTotal) => {
        onProgress(Math.round((bytesUploaded / bytesTotal) * 100));
      },
      onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
      onSuccess: () => resolve(),
    });

    if (signal) {
      signal.addEventListener("abort", () => upload.abort());
    }

    upload.start();
  });
}

export function useUpload(options: UseUploadOptions = {}) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState(0);

  const uploadFile = useCallback(
    async (file: File): Promise<UploadResponse | null> => {
      // Validate before doing anything
      const validationError = validateFile(file);
      if (validationError) {
        const err = new Error(validationError);
        setError(err);
        options.onError?.(err);
        return null;
      }

      setIsUploading(true);
      setError(null);
      setProgress(0);

      let lastError: Error = new Error("Upload failed");

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          if (attempt > 0) {
            // Brief pause before retry
            await new Promise((r) => setTimeout(r, 1500 * attempt));
          }

          setProgress(5);
          const urlRes = await apiRequest("POST", "/api/cloudflare/upload-url", {
            maxDurationSeconds: 3600,
            fileSize: file.size,
          });
          const urlData = await urlRes.json();
          const { streamId } = urlData;

          if (!streamId) {
            throw new Error("Failed to get upload URL from server.");
          }

          setProgress(10);

          const onUploadProgress = (pct: number) => {
            setProgress(10 + Math.round(pct * 0.9)); // scale 10–100
          };

          // The server decides POST vs TUS based on file size (TUS_THRESHOLD_BYTES
          // in server/routes.ts) — we dispatch on its response rather than
          // re-deriving the threshold here, so the two can't drift out of sync.
          if (urlData.method === "tus") {
            if (!urlData.tusEndpoint) throw new Error("Failed to get upload URL from server.");
            await tusUpload(urlData.tusEndpoint, file, onUploadProgress);
          } else {
            if (!urlData.uploadURL) throw new Error("Failed to get upload URL from server.");
            await xhrUpload(urlData.uploadURL, file, onUploadProgress);
          }

          setProgress(100);

          const metadata = {
            name: file.name,
            size: file.size,
            contentType: file.type || "video/mp4",
          };

          const result: UploadResponse = { cloudflareStreamId: streamId, metadata };
          options.onSuccess?.(result);
          return result;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error("Upload failed");
          console.error(`Upload attempt ${attempt + 1} failed:`, lastError.message);

          // Don't retry on validation-style or deliberate-abort errors, or on a
          // timeout — a timeout now means something is actually wrong (dead
          // connection or unusually slow server response), and blindly re-uploading
          // a large file from scratch is expensive and likely to fail the same way.
          if (
            lastError.message.includes("cancelled") ||
            lastError.message.includes("Unsupported file type") ||
            lastError.message.includes("too large") ||
            lastError.message.includes("timed out")
          ) {
            break;
          }
        }
      }

      setError(lastError);
      options.onError?.(lastError);
      setIsUploading(false);
      return null;
    },
    [options]
  );

  return {
    uploadFile,
    isUploading,
    error,
    progress,
  };
}
